import { useState, useRef, useEffect } from "react";
import { Users, X, Ban } from "lucide-react";
import Input from "./Input";

function getColor(empId) {
  let hash = 0;
  for (let i = 0; i < empId.length; i++) {
    hash = empId.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }
  const hue = (Math.abs(hash) * 137) % 360;
  return `hsl(${hue}, 65%, 55%)`;
}

function TaskEmployees({
  selected = [],
  onChange,
  employees = [],
  className = "",
  showDropdown = true,
  hideSelected = false,
  placement = "bottom",
  align = "left",
  showClearAll = true,
}) {
  const [show, setShow] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [search, setSearch] = useState("");

  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!showDropdown) return;

    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShow(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showDropdown]);

  const toggleEmployee = (id) => {
    const exists = selected.some((e) => String(e) === String(id));

    if (exists) {
      const updated = selected.filter((e) => String(e) !== String(id));
      onChange(updated);
    } else {
      const updated = [...selected, id];
      onChange(updated);
    }
  };

  const clearAll = () => {
    onChange([]);
  };

  const positionClass = placement === "top" ? "bottom-full mb-2" : "top-full mt-2";
  const alignClass =
    align === "right" ? "right-0" : align === "center" ? "left-1/2 -translate-x-1/2" : "left-0";

  const filteredEmployees = (employees || []).filter((emp) => {
    if (!emp) return false;

    if (
      emp.employeeStatus === "Resigned" ||
      emp.employeeStatus === "Terminated"
    ) {
      return false;
    }

    const fullName = `${emp.firstName || ""} ${emp.lastName || ""}`
      .trim()
      .toLowerCase();

    return (
      fullName.includes(search.toLowerCase()) ||
      emp.officeEmail?.toLowerCase().includes(search.toLowerCase())
    );
  });

  const handleDone = () => {
    setShow(false);
  };

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef} >
      <div
        className="flex items-center gap-2 cursor-pointer flex-wrap"
        onClick={() => showDropdown && setShow((prev) => !prev)}
      >
        {selected.map((empId) => {
          const emp = employees.find(
            (e) => String(e.employeeId) === String(empId)
          );
          if (!emp) return null;

          const name = `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.fullName || emp.officeEmail;
          const initials = name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((n) => n[0].toUpperCase())
            .join("");

          return (
            <div
              key={emp.employeeId}
              className="relative"
              onMouseEnter={() => setHoveredId(emp.employeeId)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div
                className="w-6 h-6 sm:w-7 sm:h-7 rounded-full overflow-hidden flex items-center justify-center text-[10px] sm:text-xs font-bold" style={{
                  backgroundColor: emp.image ? "transparent" : getColor(emp.employeeId),
                  color: "white",
                }}
              >
                {emp.image ? <img src={emp.image} alt={name} className="w-full h-full object-cover" /> : initials}
              </div>

              {hoveredId === emp.employeeId && (
                <div className="pointer-events-none absolute -top-[38px] left-1/2 -translate-x-1/2 z-50">
                  <div className="relative bg-black text-white text-[12px] px-3 py-1 rounded-full whitespace-nowrap shadow-lg">
                    {name}
                    <span className="absolute left-1/2 top-full -translate-x-1/2 w-0 h-0
                      border-l-4 border-r-4 border-t-4 border-transparent border-t-black" />
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {!hideSelected && (
          <Users
            className="
    text-gray-600
    border border-gray-300
    rounded
    p-[4px]
    sm:p-1
    bg-white
    w-[24px] h-[24px] sm:w-[26px] sm:h-[26px]
  "
          />
        )}
        {hideSelected && selected.length === 0 && <span className="text-gray-400 font-medium">-</span>}
      </div>

      {showDropdown && show && (
        <div
          className={`absolute ${positionClass} ${alignClass} 
          w-52 sm:w-64 max-h-56 sm:max-h-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50 flex flex-col`}
        >
          <div className="p-3 border-b border-gray-200">
            <Input
              type="search"
              value={search}
              placeholder="Search employees..."
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm border text-black border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="overflow-y-auto flex-1 min-h-[100px]">
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map((emp) => {
                const isSelected = selected.some(
                  (id) => String(id) === String(emp.employeeId)
                );
                const name = `${emp.firstName || ""} ${emp.lastName || ""}`.trim() || emp.officeEmail;
                const initials = name
                  .split(" ")
                  .filter(Boolean)
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase();

                return (
                  <div
                    key={emp.employeeId}
                    className={`flex items-center gap-2 px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm cursor-pointer rounded hover:bg-gray-100 relative ${isSelected ? "bg-gray-100" : ""
                      }`}
                    onClick={() => toggleEmployee(emp.employeeId)}
                  >
                    <div
                      className="w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[8px] sm:text-[10px] font-medium"
                      style={{
                        backgroundColor: getColor(emp.employeeId),
                        color: "white",
                        border: isSelected ? "2px solid #2563eb" : "none",
                      }}
                    >
                      {initials}
                    </div>

                    <span className="flex-1 truncate text-black">{name}</span>

                    {isSelected && (
                      <span
                        className="absolute top-1 right-1 flex items-center justify-center
             w-4 h-4 
             bg-red-500 text-white rounded-full"
                        onClick={(e) => {
                          e.stopPropagation();
                          onChange(
                            selected.filter((id) => String(id) !== String(emp.employeeId))
                          );
                        }}
                      >
                        <X size={12} />
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-3 text-gray-500 text-center text-sm">No employees found</div>
            )}
          </div>

          <div className="border-t border-gray-200 flex">
            {showClearAll && selected.length > 0 && (
              <div
                className="flex-1 flex items-center justify-center gap-2 px-2 py-1 sm:px-2 sm:py-2 text-xs sm:text-sm cursor-pointer
                 hover:bg-red-100 text-red-600 transition-colors"
                onClick={clearAll}
              >
                <Ban size={11} />
                <span className="text-[13px] sm:text-[15px]">Clear </span>
              </div>
            )}

            <div
              className="flex-1 flex items-center justify-center gap-2 px-2 py-1 sm:px-2 sm:py-2 text-xs sm:text-sm cursor-pointer
               hover:bg-green-100 text-green-600 transition-colors font-medium"
              onClick={handleDone}
            >
              ✓
              <span className="text-[13px] sm:text-[15px]">Done</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskEmployees;
